import { createSignal } from 'solid-js';
import { createStore } from 'solid-js/store';
import { useNavigate } from '@solidjs/router';
import { FormProps, FormValue, MauticPostAction, FieldAttributes } from './mautic-form-types';
import { useAppContext } from '../../app-context-provider/app-context-provider';
import { validate } from '../../tools/form-validation';

const FieldType = {
    CHECKBOX: 'checkboxgrp',
    RADIO: 'radiogrp',
} as const;

export const useMauticForm = (props: FormProps) => {
    const navigate = useNavigate();
    const { translate, turnstileSiteKey } = useAppContext();

    const fieldMap = new Map(props.form.fields.map(f => [f.alias, f]));

    const [errors, setErrors] = createStore<{ [key: string]: string }>({});
    const [loading, setLoading] = createSignal(false);
    const [success, setSuccess] = createSignal<string | null>(null);
    const [touched, setTouched] = createStore<{ [key: string]: boolean }>({});

    // Cloudflare Turnstile. Only enforced when a site key is configured.
    const [turnstileToken, setTurnstileToken] = createSignal('');
    // Set when the CF script never loads (ad blocker, network policy, outage).
    // We still refuse to submit — dropping the check would let a bot skip the
    // captcha by blocking the script — but the user gets told why.
    const [turnstileUnavailable, setTurnstileUnavailable] = createSignal(false);
    let turnstileReset: (() => void) | undefined;
    const registerTurnstileReset = (reset: () => void) => {
        turnstileReset = reset;
    };

    const getInitialValues = (): FormValue => {
        if (!props.form.fields) {
            return {};
        }

        return props.form.fields.reduce((obj: FormValue, field) => {
            const key = field.alias;
            const hasMultipleOptions = field.properties?.optionlist?.list && field.properties.optionlist.list.length > 1;

            switch (field.type) {
                case 'number':
                    obj[key] = 0;
                    break;
                case FieldType.CHECKBOX:
                    obj[key] = hasMultipleOptions ? [] : false;
                    break;
                case FieldType.RADIO:
                    obj[key] = hasMultipleOptions ? '' : false;
                    break;
                default:
                    obj[key] = '';
                    break;
            }

            return obj;
        }, {});
    };

    const [formValues, setFormValues] = createStore<FormValue>(getInitialValues());

    const handleChange = (key: string, value: string | string[] | boolean | number) => {
        setFormValues(key, value);
        setTouched(key, true);
    };

    const revalidateField = (key: string, value: string | boolean | string[] | number, validationMessage = '') => {
        const field = fieldMap.get(key);

        if ((value === '' || value === null || value === undefined) && !errors[key] && !touched[key]) {
            return;
        }

        if (field?.isRequired && (value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0))) {
            setErrors(key, field.validationMessage || `${field.label} ${translate('is-required')}`);
        } else {
            setErrors(key, '');
        }

        if (!errors[key] && typeof value === 'string' && field?.leadField) {
            const { valid, errorMessage } = validate(field.leadField, value);
            if (!valid && errorMessage) {
                setErrors(key, translate(errorMessage));
                return;
            }
        }

        if (validationMessage?.trim()) {
            setErrors(key, validationMessage);
        }
    };

    const validateForm = (): boolean => {
        let isValid = true;
        const newErrors: { [key: string]: string } = {};

        fieldMap.forEach((field, key) => {
            const value = formValues[key];

            if (field.isRequired && (!value || (Array.isArray(value) && value.length === 0))) {
                newErrors[key] = field.validationMessage ?? `${field.label} ${translate('is-required')}`;
                isValid = false;
            }
        });

        setErrors(newErrors);
        return isValid;
    };

    const handleSubmit = async (e: SubmitEvent) => {
        e.preventDefault();

        setLoading(true);
        setErrors({});
        // Store setter merges top-level keys, so an empty object won't clear a
        // previously-set form-level error — reset it explicitly.
        setErrors('_form', '');

        if (!validateForm()) {
            setLoading(false);
            const firstInvalid = document.querySelector<HTMLElement>('[aria-invalid="true"]');
            firstInvalid?.focus();
            return;
        }

        if (turnstileSiteKey && !turnstileToken()) {
            setErrors('_form', translate(turnstileUnavailable() ? 'captcha-unavailable' : 'captcha-required'));
            setLoading(false);
            return;
        }

        try {
            const formData = new FormData();

            const values = Object.fromEntries(
                Object.entries(formValues)
                    .map(([key, value]) => [key, Array.isArray(value) ? [...value] : value])
                    .filter(([, value]) => value !== '' && value !== null && !(Array.isArray(value) && value.length === 0))
            );

            for (const [key, value] of Object.entries(values)) {
                const stringValue = typeof value === 'object' ? JSON.stringify(value) : value;
                formData.append(`mauticform[${key}]`, stringValue?.toString() ?? '');
            }

            formData.append('mauticform[formId]', props.form.id);
            formData.append('mauticform[return]', props.form.return);
            formData.append('mauticform[formName]', props.form.name);
            formData.append('mauticform[messenger]', props.form.messenger);

            const response = await fetch('/postform', {
                method: 'POST',
                headers: {
                    'x-form-id': props.form.id,
                    ...(turnstileToken() ? { 'x-turnstile-token': turnstileToken() } : {}),
                },
                body: formData,
            });

            // The /postform proxy rejects with 403 when Turnstile verification
            // fails. Tokens are single-use, so reset the widget and let the user
            // try again.
            if (response.status === 403) {
                turnstileReset?.();
                setErrors('_form', translate('captcha-failed'));
                return;
            }

            if (!response.ok) {
                throw new Error(`Server responded with status ${response.status}`);
            }

            window.gtag?.('event', 'form_submit', {
                /* eslint-disable camelcase */
                event_category: 'form',
                event_label: props.form.name,
                form_id: props.form.id,
                /* eslint-enable camelcase */
            });

            // Push a proper dataLayer event so GTM Custom Event triggers fire.
            // gtag('event', ...) only pushes an arguments array, which has no
            // `event` key for a Custom Event trigger to match on.
            window.dataLayer?.push({
                /* eslint-disable camelcase */
                event: 'form_submit',
                form_id: props.form.id,
                form_name: props.form.name,
                /* eslint-enable camelcase */
            });

            if (props.form.postAction === MauticPostAction.Redirect && props.form.postActionProperty) {
                const redirectUrl = props.form.postActionProperty.startsWith('/')
                    ? props.form.postActionProperty
                    : new URL(props.form.postActionProperty, window.location.origin).pathname;

                navigate(redirectUrl);
            }

            setSuccess(props.form.postAction === MauticPostAction.Message ? props.form.postActionProperty : translate('form-sent'));
            setFormValues(getInitialValues());
            turnstileReset?.();

        } catch (error) {
            // Show users a friendly message; keep the raw error in the console
            // for debugging rather than surfacing "status 500" etc. in the UI.
            console.error('Mautic form submit failed', error);
            setErrors('_form', translate('form-error'));
        } finally {
            setLoading(false);
        }
    };

    const getFieldProps = (field: FieldAttributes) => ({
        ...field,
        errors,
        value: formValues[field.alias],
        variant: props.variant ?? 'dark',
        handleChange,
        revalidate: revalidateField,
    });

    return {
        errors,
        loading,
        success,
        formValues,
        handleSubmit,
        getFieldProps,
        turnstileSiteKey,
        setTurnstileToken,
        registerTurnstileReset,
        turnstileUnavailable,
        setTurnstileUnavailable,
    };
};
